import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { downloadAuthFile } from "../../lib/fileOpen";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { FilePdf, EnvelopeSimple, PencilSimple, DownloadSimple } from "@phosphor-icons/react";
import { PortalPushButton, PortalLiveBadge, ClientDecisionBanner, fmtMoney } from "./PortalQuoteActions";
import { QuoteNarrative } from "../portal/QuoteNarrative";
import QuoteEmailDialog from "./QuoteEmailDialog";
import QuoteApprovalLinkButton from "../QuoteApprovalLinkButton";

export const QUOTE_STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  sent: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  converted: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  cancelled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

function PdfPreview({ quoteId, active }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!active || url) return;
    let alive = true;
    API.get(`/quotes/${quoteId}/pdf?view=client`, { responseType: "blob" })
      .then((res) => { if (alive) setUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))); })
      .catch(() => alive && setErr("Could not render the PDF preview."));
    return () => { alive = false; };
  }, [active, quoteId, url]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (err) return <p className="text-sm text-destructive p-4" data-testid="quote-drawer-pdf-error">{err}</p>;
  if (!url) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;
  return <iframe title="Quote PDF" src={`${url}#toolbar=0&view=FitH`} className="w-full h-[70vh] rounded-md border border-border bg-white" data-testid="quote-drawer-pdf-frame" />;
}

function Summary({ q }) {
  const addr = [q.property_name, q.street_address, q.town_city, q.postcode].filter(Boolean).join(", ");
  return (
    <div className="space-y-4" data-testid="quote-drawer-summary">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Client</p><p className="font-medium">{q.client_name || "—"}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Property</p><p className="font-medium">{addr || "—"}</p></div>
        {q.contact_name && <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Contact</p><p className="font-medium">{q.contact_name}</p></div>}
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Valid for</p><p className="font-medium">{q.validity_days || 30} days</p></div>
      </div>
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-3 py-2 font-semibold">Item</th><th className="text-right px-3 py-2 font-semibold w-14">Qty</th><th className="text-right px-3 py-2 font-semibold w-28">Total</th></tr>
          </thead>
          <tbody>
            {(q.line_items || []).map((li, i) => (
              <tr key={i} className="border-t border-border" data-testid={`quote-drawer-line-${i}`}>
                <td className="px-3 py-2"><p className="font-medium">{li.item_name || li.description || "Item"}</p>{li.item_name && li.description && <p className="text-xs text-muted-foreground">{li.description}</p>}</td>
                <td className="px-3 py-2 text-right">{li.quantity ?? 1}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtMoney(li.total)}</td>
              </tr>
            ))}
            {!(q.line_items || []).length && <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground text-xs">No line items yet</td></tr>}
          </tbody>
        </table>
        <div className="border-t border-border bg-muted/30 px-3 py-2 text-sm space-y-1">
          <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmtMoney(q.grand_total)}</span></div>
          {q.vat_enabled && <div className="flex justify-between text-muted-foreground"><span>VAT ({q.vat_rate ?? 20}%)</span><span>{fmtMoney(q.vat_amount)}</span></div>}
          <div className="flex justify-between font-semibold text-base"><span>Total{q.vat_enabled ? " inc. VAT" : ""}</span><span data-testid="quote-drawer-total">{fmtMoney(q.vat_enabled ? q.total_inc_vat : q.grand_total)}</span></div>
        </div>
      </div>
      <QuoteNarrative quote={q} />
      {q.notes && <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</p><p className="text-sm whitespace-pre-wrap">{q.notes}</p></div>}
    </div>
  );
}

/* Side drawer: the full quote (summary + client PDF) with every action, without leaving the request / job page. */
export default function QuoteDrawer({ quoteId, open, onOpenChange, onChanged }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [tab, setTab] = useState("summary");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => quoteId && API.get(`/quotes/${quoteId}`).then(({ data }) => setQ(data)).catch(() => setQ(null));
  useEffect(() => {
    if (!open) return;
    setQ(null); setTab("summary");
    load();
    API.get("/settings/quote-email").then(({ data }) => setEmailEnabled(!!data.enabled)).catch(() => {});
  }, [open, quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(); onChanged?.(); };
  const download = async (view) => {
    setBusy(view);
    await downloadAuthFile(`/quotes/${quoteId}/pdf?view=${view}`, `${q?.reference || "quote"}_${view}.pdf`);
    setBusy("");
  };
  const goFull = () => { onOpenChange(false); navigate(`/quotes/${quoteId}`); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="quote-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span data-testid="quote-drawer-ref">{q?.reference || "Quote"}</span>
            {q && <Badge className={`text-[10px] ${QUOTE_STATUS_STYLES[q.status] || ""}`} data-testid="quote-drawer-status">{q.status?.toUpperCase()}</Badge>}
            {q?.portal_sent_at && <PortalLiveBadge pushedAt={q.portal_sent_at} notified={q.portal_notified} withdrawUrl={`/quotes/${quoteId}/withdraw-from-portal`} onDone={refresh} testId="quote-drawer-portal-live" />}
          </SheetTitle>
          <SheetDescription data-testid="quote-drawer-title">{q?.title || (q ? "" : "Loading quote…")}</SheetDescription>
        </SheetHeader>

        {q && (
          <>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="quote-drawer-actions">
              <Button size="sm" variant="outline" onClick={() => download("client")} disabled={busy === "client"} data-testid="quote-drawer-pdf-client"><FilePdf size={14} className="mr-1" /> {busy === "client" ? "Preparing…" : "Client PDF"}</Button>
              <Button size="sm" variant="outline" onClick={() => download("admin")} disabled={busy === "admin"} data-testid="quote-drawer-pdf-admin"><DownloadSimple size={14} className="mr-1" /> Admin PDF</Button>
              {emailEnabled && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setEmailOpen(true)} data-testid="quote-drawer-email"><EnvelopeSimple size={14} className="mr-1" /> Email Quote</Button>}
              {!q.portal_sent_at && !q.client_response && ["draft", "sent", "approved", "declined", "expired"].includes(q.status) && (
                <PortalPushButton url={`/quotes/${quoteId}/send-to-portal`} amount={q.grand_total} onDone={refresh} testId="quote-drawer-send-to-portal" disabledReason={!q.client_id ? "Attach this quote to a client first" : ""} />
              )}
              <QuoteApprovalLinkButton quote={q} onSent={refresh} />
              <Button size="sm" variant="ghost" onClick={goFull} data-testid="quote-drawer-open-full"><PencilSimple size={14} className="mr-1" /> Open &amp; edit</Button>
            </div>
            <ClientDecisionBanner response={q.client_response} amount={q.grand_total} testId="quote-drawer-decision" />

            <Tabs value={tab} onValueChange={setTab} className="mt-4">
              <TabsList className="grid grid-cols-2 w-full sm:w-72">
                <TabsTrigger value="summary" data-testid="quote-drawer-tab-summary">Summary</TabsTrigger>
                <TabsTrigger value="pdf" data-testid="quote-drawer-tab-pdf">PDF preview</TabsTrigger>
              </TabsList>
              <TabsContent value="summary" className="mt-3"><Summary q={q} /></TabsContent>
              <TabsContent value="pdf" className="mt-3"><PdfPreview quoteId={quoteId} active={tab === "pdf"} /></TabsContent>
            </Tabs>
          </>
        )}
        {!q && open && <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>}
        <QuoteEmailDialog quoteId={quoteId} open={emailOpen} onOpenChange={setEmailOpen} onSent={refresh} />
      </SheetContent>
    </Sheet>
  );
}
